import { FileWarning, ListTree } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';

import { StatePanel } from '@/components/ui/StatePanel';

import type { EntityCopy } from '../copy';
import type { StatusFilter } from '../use-catalog-view';
import { CatalogSkeleton } from './CatalogSkeleton';
import { errorAdvice } from '@/lib/error-advice';

interface Props {
  copy: EntityCopy;
  query: {
    isLoading: boolean;
    isError: boolean;
    error: unknown;
    data: unknown[] | undefined;
    refetch: () => unknown;
  };
  /** Filas visibles tras los filtros. */
  visible: number;
  status: StatusFilter;
  /** Hay un filtro aparte del estado (p. ej. la norma). */
  extraFilter: boolean;
  onCreate: () => void;
  onClearFilters: () => void;
  children: ReactNode;
}

/** Estados de un catálogo (UX-LOAD-01, UX-ERR-01, §1.7): carga con skeleton, error
 *  con reintento, vacío que enseña y ofrece crear, y filtro sin resultados distinto
 *  del catálogo vacío. Con datos, la tabla va en su tarjeta. */
export function CatalogBody({
  copy,
  query,
  visible,
  status,
  extraFilter,
  onCreate,
  onClearFilters,
  children,
}: Props): ReactElement {
  if (query.isLoading) {
    return <CatalogSkeleton />;
  }
  if (query.isError || query.data === undefined) {
    return (
      <StatePanel
        icon={<FileWarning />}
        title="No se pudo cargar el catálogo"
        tone="danger"
        role="alert"
        onRetry={() => void query.refetch()}
      >
        {errorAdvice(query.error)}
      </StatePanel>
    );
  }
  if (query.data.length === 0) {
    return (
      <StatePanel icon={<ListTree />} title={copy.emptyTitle} role="status">
        {copy.emptyBody}{' '}
        <button type="button" className="btn sm" onClick={onCreate}>
          {copy.newLabel}
        </button>
      </StatePanel>
    );
  }
  if (visible === 0) {
    const title =
      status === 'todos' || extraFilter
        ? `Ningún resultado con estos filtros`
        : copy.noneWith(status === 'activos' ? 'activo' : 'desactivado');
    return (
      <StatePanel icon={<ListTree />} title={title} role="status">
        Cambia el filtro para ver el resto del catálogo.{' '}
        <button type="button" className="btn sm mut" onClick={onClearFilters}>
          Ver todos
        </button>
      </StatePanel>
    );
  }
  return <div className="card rise">{children}</div>;
}
